<script>
  import { computed, useFetch, useRoute } from '@nuxtjs/composition-api'
  import { productGetters } from '@/composables/propeller/getters'
  import { useProduct } from '@/composables/propeller/useProduct'

  import ProductsDetail from '@/components/Ecommerce/Products/ProductsDetail'
  import ProductsDetailSkeleton from '@/components/Ecommerce/Products/ProductsDetailSkeleton'
  import { HeadMeta } from '@/utils/head-meta'

  export default {
    components: {
      ProductsDetail,
      ProductsDetailSkeleton
    },
    setup (props, { root }) {
      const route = useRoute()
      const { products, search, loading } = useProduct(`products-${route.value.params.id}`)
      const slug = computed(() => productGetters.getSlug(products.value))

      useFetch(async () => {
        await search({ id: route.value.params.id })

        // `search` leaves `products` as an empty object when the id resolves to
        // nothing, or to a product outside the Examenbundel catalogue. `{}` is
        // truthy, so this guard never fired and such a page rendered as a normal
        // (orderable) product — see useProduct.
        if (!products.value?.productId) {
          return root.context.error({
            statusCode: 404,
            message: 'This product could not be found'
          })
        }

        if (route.value.params.slug !== slug.value) {
          root.context.redirect(301, `/producten/${route.value.params.id}/${slug.value}`)
        }
      })

      const product = computed(() => {
        return !loading.value
          ? products.value
          : null
      })

      // Meta reads `products` directly, NOT the `loading`-gated `product` above.
      // On hydration the client refetch flips `product` to null, `head()` then
      // rendered an empty title and the tab was left showing a bare
      // "| Webshop Examenbundel" even though the server had sent the right one.
      const meta = computed(() => ({
        name: productGetters.getName(products.value),
        image: productGetters.getCoverImage(products.value),
        description: productGetters.getShortDescription(products.value)
      }))

      return {
        loading,
        product,
        meta
      }
    },
    head () {
      if (!this.meta?.name) {
        return
      }

      return HeadMeta({
        title: this.meta.name,
        titleTemplate: '%s | Webshop Examenbundel',
        productImage: this.meta.image,
        metaDescription: this.meta.description.replace(/(<([^>]+)>)/gi, '')
      })
    }
  }
</script>

<template>
  <div>
    <transition
      name="fade"
      mode="out-in"
      class="relative"
    >
      <ProductsDetailSkeleton v-if="!product" />
      <ProductsDetail
        v-else
        :product="product"
      />
    </transition>
  </div>
</template>

<style scoped lang="scss">
.product-detail-grid {
  @apply grid grid-cols-1 grid-rows-3 gap-x-8 gap-y-14 grid-rows-max;
  @screen sm {
    @apply grid-cols-2 grid-rows-2;
  }
  grid-template-rows: auto 1fr !important;
}
</style>
